import { Box, Button, Link, Typography } from '@mui/material';
import Image from 'next/image';
import type React from 'react';
import type { StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import type { ColorToken } from '../../types';
import { MarketplaceEntityCTA } from './MarketplaceEntityCTA';

const illustrations = {
  cashbackCC: {
    label: 'Cashback CC',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/cashback-cc-desktop.png',
    alt: 'One blue credit card with a green card behind it.',
  },
  coinsCC: {
    label: 'CC Coins',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/creditcard-coins-desktop.png',
    alt: 'One blue credit card on a flat surface with coins on both sides.',
  },
  greenCC: {
    label: 'Green CC',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/creditcard-desktop.png',
    alt: 'A green credit card.',
  },
  travelCC: {
    label: 'Travel CC',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/travel-cc-desktop.png',
    alt: 'A blue credit card with a golden plane at the bottom and a palm tree at the top.',
  },
  bankDeposit: {
    label: 'Bank Deposit',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/bank-deposit-desktop.png',
    alt: 'A green bank that has a coin slot at the top where a hand is depositing a coin.',
  },
  bank: {
    label: 'Bank',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/bank-desktop.png',
    alt: 'A green bank.',
  },
  womanDevice: {
    label: 'Woman Device',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/woman-device-desktop.png',
    alt: 'A person looking at a mobile phone.',
  },
  cashCoins: {
    label: 'Cash Coins',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/cash-coins-desktop.png',
    alt: 'Cash and coins.',
  },
  cashPercent: {
    label: 'Cash Percent',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/cash-percent-desktop.png',
    alt: 'Cash with a green percentage sign on the top-right corner.',
  },
  approvedDocuments: {
    label: 'Approved Documents',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/approved-documents-desktop.png',
    alt: 'Paper documents wrapped with a ribbon that has a checkmark on it.',
  },
  cashCalculator: {
    label: 'Cash Calculator',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2023/12/calculator-desktop.png',
    alt: 'A calculator with cash next to it.',
  },
  womanSitting: {
    label: 'Woman Sitting',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/woman-sitting-desktop.png',
    alt: 'Woman sitting on top of some coins.',
  },
  walletCard: {
    label: 'Wallet Card',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/wallet-card-desktop.png',
    alt: 'Wallet with a gold credit card.',
  },
  storefront: {
    label: 'Storefront',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/storefront-desktop.png',
    alt: 'Storefront with a door and window.',
  },
  storeSign: {
    label: 'Store Sign',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/store-sign-desktop.png',
    alt: 'Sign with a "we are open" text.',
  },
  savingMoney: {
    label: 'Saving Money',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/saving-money-desktop.png',
    alt: 'Jar with some coins inside and some cash on the side.',
  },
  moneyCard: {
    label: 'Money Card',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/money-card-desktop.png',
    alt: 'Some cash and a credit card.',
  },
  laptopSearch: {
    label: 'Laptop Search',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/laptop-search-desktop.png',
    alt: 'Couple of hands typing on a laptop.',
  },
  handPayment: {
    label: 'Hand Payment',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/hand-payment-desktop.png',
    alt: 'Hand with a blue credit card.',
  },
  holdingCard: {
    label: 'Holding Card',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/hand-holding-card-desktop.png',
    alt: 'Hand holding a credit card.',
  },
  creditCard: {
    label: 'Credit Card',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/generic-credit-card-desktop.png',
    alt: 'Red credit card standing alone.',
  },
  genericCard: {
    label: 'Generic Card',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/generic-card-desktop.png',
    alt: 'A note, a pencil, and a green credit card.',
  },
  cardPayment: {
    label: 'Card Payment',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/02/card-payment-desktop.png',
    alt: 'Some bills and a red card.',
  },
  upwardChart: {
    label: 'Upward Chart',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/upward-chart.png',
    alt: 'Laptop with upward chart.',
  },
  upwardChartSpot: {
    label: 'Upward Chart Spot',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/upward-chart-spot.png',
    alt: 'Green circle with upward chart.',
  },
  shoppingCart: {
    label: 'Shopping Cart',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/shopping-cart.png',
    alt: 'Green shopping card over cellphone.',
  },
  shoppingBagSpot: {
    label: 'Shopping Bag Spot',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/shopping-bag-spot.png',
    alt: 'Green shopping bag.',
  },
  restaurant: {
    label: 'Restaurant',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/restaurant.png',
    alt: 'Green restaurant.',
  },
  restaurantSpot: {
    label: 'Restaurant Spot',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/restaurant-spot.png',
    alt: 'Blue restaurant.',
  },
  paycheckSpot: {
    label: 'Paycheck Spot',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/paycheck-spot.png',
    alt: 'Green paycheck.',
  },
  invoiceSpot: {
    label: 'Invoice Spot',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/invoice-spot.png',
    alt: 'Blue invoice.',
  },
  calculatorHandSpot: {
    label: 'Calculator Hand Spot',
    url: 'https://assets.nerdwallet.com/blog/wp-content/uploads/2024/03/calculator-hand-spot.png',
    alt: 'Hand with blue calculator.',
  },
};

type IllustrationKey = keyof typeof illustrations;

type IllustrationCardListProps = StructuredContentDocument<{
  title: string;
  subtitle?: string;
  backgroundColor?: ColorToken;
  items: Array<{
    illustration: IllustrationKey;
    headline?: string;
    subtext?: string;
  }>;
  ctaHref?: string;
  ctaText?: string;
  marketplaceEntityId: string;
  redirectToPartner?: boolean;
  hasBorder?: boolean;
}>;

const MAX_ITEMS = 4;

const Illustration = ({ illustration }: { illustration: IllustrationKey }): React.JSX.Element => {
  const { url, alt } = illustrations[illustration];

  return (
    <Box sx={{ maxHeight: { xs: 90, md: 110 }, maxWidth: { xs: 90, md: 110 } }}>
      <Image alt={alt} src={url} height={110} width={110} />
    </Box>
  );
};

export const SMBIllustrationCardList = ({
  props,
}: IllustrationCardListProps): React.JSX.Element => {
  const {
    title,
    subtitle,
    backgroundColor = 'transparent',
    items,
    ctaHref,
    ctaText,
    marketplaceEntityId,
    redirectToPartner,
    hasBorder,
  } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 3,
        padding: 3,
        border: hasBorder ? '1px solid' : '0px',
        borderColor: 'neutral.lighter',
        bgcolor: backgroundColor.replace('-', '.'),
      }}
    >
      <Typography variant="titleSmall" align="center">
        {title}
      </Typography>
      {subtitle && <Typography variant="textBold">{subtitle}</Typography>}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
          gap: 3,
        }}
      >
        {items?.slice(0, MAX_ITEMS).map(
          ({ illustration, headline, subtext }) =>
            illustrations?.[illustration] && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
                key={`${illustration}-${headline}`}
              >
                <Illustration illustration={illustration} />
                {headline && (
                  <Typography align="center" variant="textBold">
                    {headline}
                  </Typography>
                )}
                {subtext && <Typography align="center">{subtext}</Typography>}
              </Box>
            ),
        )}
      </Box>
      {redirectToPartner && marketplaceEntityId && (
        <MarketplaceEntityCTA marketplaceEntityId={marketplaceEntityId} text={ctaText} />
      )}
      {!redirectToPartner && ctaHref && ctaText && (
        <Link href={ctaHref}>
          <Button>{ctaText}</Button>
        </Link>
      )}
    </Box>
  );
};
